import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '@/config/api'
import type { ThreadSummary, Pagination, AnalysisStartResponse, AnalysisStatusResponse, AnalysisStatus } from '@/types'

interface AllThreadsResponse {
  threads: (ThreadSummary & { user_id: string })[]
  pagination: Pagination
}

export function useAllThreads(
  page = 1,
  limit = 20,
  isActive?: boolean,
  hasAnalysis?: boolean,
  userId?: string
) {
  const [threads, setThreads] = useState<(ThreadSummary & { user_id: string })[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, unknown> = { page, limit }
      if (isActive !== undefined) params.is_active = isActive
      if (hasAnalysis !== undefined) params.has_analysis = hasAnalysis
      if (userId) params.user_id = userId

      const { data } = await api.get<AllThreadsResponse>('/api/threads', { params })
      setThreads(data.threads)
      setPagination(data.pagination)
    } catch (err) {
      setError((err as Error).message)
      console.error('Failed to fetch all threads:', err)
    } finally {
      setLoading(false)
    }
  }, [page, limit, isActive, hasAnalysis, userId])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { threads, pagination, loading, error, refetch }
}

const POLL_INTERVAL = 2000

interface AnalysisState {
  status: AnalysisStatus
  error?: string
}

export function useRequestThreadAnalysis() {
  const [analysisStates, setAnalysisStates] = useState<Map<number, AnalysisState>>(new Map())
  const pollIntervalsRef = useRef<Map<number, ReturnType<typeof setInterval>>>(new Map())

  // 특정 스레드의 폴링 정리
  const clearPollingForThread = useCallback((threadId: number) => {
    const interval = pollIntervalsRef.current.get(threadId)
    if (interval) {
      clearInterval(interval)
      pollIntervalsRef.current.delete(threadId)
    }
  }, [])

  // 폴링 시작
  const startPolling = useCallback((threadId: number, onComplete?: () => void) => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get<AnalysisStatusResponse>(`/api/analysis/thread/${threadId}/status`)

        if (data.status === 'completed') {
          clearPollingForThread(threadId)
          setAnalysisStates((prev) => {
            const next = new Map(prev)
            next.delete(threadId)
            return next
          })
          onComplete?.()
        } else if (data.status === 'failed') {
          clearPollingForThread(threadId)
          setAnalysisStates((prev) => {
            const next = new Map(prev)
            next.set(threadId, { status: 'failed', error: data.error_message || '분석 실패' })
            return next
          })
        } else {
          setAnalysisStates((prev) => {
            const next = new Map(prev)
            next.set(threadId, { status: data.status })
            return next
          })
        }
      } catch (err) {
        console.error('Failed to poll analysis status:', err)
      }
    }, POLL_INTERVAL)

    pollIntervalsRef.current.set(threadId, interval)
  }, [clearPollingForThread])

  const requestAnalysis = useCallback(
    async (threadId: number, forceRefresh = false, onComplete?: () => void) => {
      // 기존 폴링 정리
      clearPollingForThread(threadId)

      // 분석 중 상태로 설정
      setAnalysisStates((prev) => {
        const next = new Map(prev)
        next.set(threadId, { status: 'received' })
        return next
      })

      try {
        const { data } = await api.post<AnalysisStartResponse>(`/api/analysis/thread/${threadId}`, {
          force_refresh: forceRefresh,
        })

        // 캐시 히트 또는 이미 완료된 경우
        if (data.status === 'completed') {
          setAnalysisStates((prev) => {
            const next = new Map(prev)
            next.delete(threadId)
            return next
          })
          onComplete?.()
        } else {
          // 폴링 시작
          setAnalysisStates((prev) => {
            const next = new Map(prev)
            next.set(threadId, { status: data.status })
            return next
          })
          startPolling(threadId, onComplete)
        }
      } catch (err) {
        setAnalysisStates((prev) => {
          const next = new Map(prev)
          next.set(threadId, { status: 'failed', error: (err as Error).message })
          return next
        })
        console.error('Failed to request thread analysis:', err)
        throw err
      }
    },
    [clearPollingForThread, startPolling]
  )

  const isAnalyzing = useCallback(
    (threadId: number) => {
      const state = analysisStates.get(threadId)
      return state?.status === 'received' || state?.status === 'processing'
    },
    [analysisStates]
  )

  const getStatus = useCallback(
    (threadId: number) => analysisStates.get(threadId)?.status,
    [analysisStates]
  )

  const getError = useCallback(
    (threadId: number) => analysisStates.get(threadId)?.error,
    [analysisStates]
  )

  const analyzing = Array.from(analysisStates.values()).some(
    (state) => state.status === 'received' || state.status === 'processing'
  )

  // 컴포넌트 언마운트 시 모든 폴링 정리
  useEffect(() => {
    return () => {
      pollIntervalsRef.current.forEach((interval) => clearInterval(interval))
      pollIntervalsRef.current.clear()
    }
  }, [])

  return { requestAnalysis, analyzing, isAnalyzing, getStatus, getError }
}
